// @flow
import { CountryName } from '@gandi/react-places';
import type { Organization } from '@gandi/redux-organization';
import type { Organization as OrganizationSharing } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';
import { Base, Text, Badge, Svg } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectHasMissingOrgInfo } from 'src/modules/organization/selectors';

import type { CartOwnerCandidate } from '../types';

import styles from './Option.scss';

const SVGStyle = { height: '1rem', width: 'auto' };
const textWrapStyle = { whiteSpace: 'normal' };

type Org = CartOwnerCandidate | OrganizationSharing | Organization;

export function getDisplayName(org: Org): string {
  return org.name || org.orgname || '';
}

function useTypeLabel(type?: CartOwnerCandidate['type']) {
  const { __ } = useTranslator();

  if (!type) return null;

  if (type === 'individual') return __('Individual');
  if (type === 'company') return __('Company');
  if (type === 'association') return __('Association');
  if (type === 'publicbody') return __('Public Body');
  return __('Organization');
}

function useLegalName(org: Org): string {
  let name =
    org.firstname && org.lastname ? `${org.firstname} ${org.lastname}` : '';
  if (org.type !== 'individual') {
    const legal = org.orgname || org.name || '';
    name = name ? `${legal} / ${name}` : legal;
  }
  return name;
}

export function Option({
  org,
  isPayer,
}: {
  org: Org,
  isPayer?: boolean,
}): React.MixedElement {
  const { __ } = useTranslator();

  const missingOrgInfo = useAppSelector(
    state => !!selectHasMissingOrgInfo(state, { orgId: org.id, orga: org }),
  );

  const type = useTypeLabel(org.type);

  const legalName = useLegalName(org);
  return (
    <Base padding="small" key={`owner-${org.id}`}>
      <Base tagName="p" margin="none">
        <Base flex flexAlignItemsCenter tagName="span">
          <Text weight="semibold">{getDisplayName(org)}</Text>
          {'personal' in org && !!org.personal && (
            <Badge className={styles.badge}>{__('Me')}</Badge>
          )}
          {org.reseller && (
            <Svg
              className={styles.badge}
              title={__('Reseller')}
              name="badgeReseller"
              theme="anthracite"
              style={SVGStyle}
            />
          )}
          {org.corporate && (
            <Svg
              className={styles.badge}
              title={__('Corporate')}
              name="badgeCorporate"
              theme="anthracite"
              style={SVGStyle}
            />
          )}
        </Base>
        {type && (
          <Text color="light" size="small" transform="uppercase">
            {type}
          </Text>
        )}
        {legalName && (
          <>
            <br />
            <span>{legalName}</span>
          </>
        )}

        {org.email && (
          <>
            <br />
            <span>{org.email}</span>
          </>
        )}
        {org.country && org.city && org.streetaddr && (
          <>
            <br />
            <span style={textWrapStyle}>
              {`${org.streetaddr}${org.zip ? `, ${org.zip}` : ''}, ${
                org.city
              }, `}
              <CountryName country={org.country} />
            </span>
          </>
        )}
        {isPayer && org.type === 'company' && !missingOrgInfo && (
          <>
            <br />
            <Text color={org.vat_number ? undefined : 'success'}>
              {org.vat_number
                ? __('VAT Number %(vat_number)s', {
                    vat_number: org.vat_number,
                  })
                : __('No VAT number')}
            </Text>
          </>
        )}
        {missingOrgInfo && (
          <>
            <br />
            <Text color="success">
              {__('Required information will be asked afterwards')}
            </Text>
          </>
        )}
      </Base>
    </Base>
  );
}
